import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';
import { Download } from 'lucide-react';
import * as React from 'react';

import { SAVE_REFUSED, errorMessage, saveFile } from '@/domains/api';
import { surveyApi } from '../api';

/**
 * Downloads the PDF of one submission (survey-api's advisor PDF design §4) — the latest when
 * `submissionId` is absent. survey-api names the file; the name built from `surveyCode` and
 * `cycleCode` is for a browser that is not shown it. `context` completes the accessible name where
 * the visible label alone would not say which document it is. A refusal is said under the button,
 * and the button stays usable.
 */
export function SubmissionDownloadButton({
  responseId,
  submissionId,
  surveyCode,
  cycleCode,
  label,
  context,
  variant,
  className
}: {
  responseId: string;
  submissionId?: Nullable<number>;
  surveyCode: string;
  cycleCode: string;
  label: string;
  context?: string;
  variant?: 'default' | 'outline';
  className?: string;
}) {
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);

  const download = async () => {
    setBusy(true);
    setRefusal(null);
    try {
      const { file, fileName } = await surveyApi.submissionDocument(responseId, submissionId);
      const version = submissionId == null ? '' : `-${submissionId}`;
      if (!saveFile(file, fileName ?? `${surveyCode}-${cycleCode}-submitted${version}.pdf`)) {
        setRefusal(SAVE_REFUSED);
      }
    } catch (failure) {
      setRefusal(errorMessage(failure, 'The PDF could not be made'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn('grid gap-1', className)}>
      <Button type="button" variant={variant} onClick={() => void download()} disabled={busy}>
        <Download className="size-4" aria-hidden />
        {busy ? (
          'Preparing the PDF…'
        ) : (
          <>
            {label}
            {context && <span className="sr-only"> {context}</span>}
          </>
        )}
      </Button>
      {refusal && (
        // Wraps to the button's width rather than widening the column it sits in.
        <p role="alert" className="w-0 min-w-full text-sm text-destructive">
          {refusal}
        </p>
      )}
    </div>
  );
}
